/**
 * directory domain (doc 01 A3.1 / A3.2). Read paths for the workforce board and
 * worker hub/profile. Function names + paths mirror the route inventory verbatim.
 * Types mirror the API's `directory` views (mark for consolidation).
 *
 * Client identity is INITIALS-ONLY on every read path. The full name flows only
 * through {@link revealName} — the single audited route gated by
 * `client:reveal_name` — and the returned name must never be cached or logged.
 */
import { del, get, patch, post } from './transport.js';
/**
 * `GET /directory/workers` → `{ workers }` (needs `directory:read` AND
 * `user:read`).
 *
 * The staff roster is the list of the users in the organization, and `user:read`
 * — 'View users in the organization' — is the key that governs that fact
 * everywhere else in this API: `GET /users`, the audit log's actor names, the
 * alerts member list, and the worker identity fields inside the programs
 * payloads. 1755046800000_seed-permissions grants it to `org_admin` and
 * `supervisor` and deliberately NOT to `worker`, so a plain worker is REFUSED
 * here with a 403 naming the key — never handed a silently narrowed list.
 *
 * A caller who wants their OWN record calls `me()`, which needs no directory
 * grant at all; a caller who wants a worker they supervise calls `worker(id)`,
 * which also accepts the `worker.supervisor_id` edge.
 */
export async function workers(options) {
    const body = await get('/directory/workers', options);
    return body.workers;
}
/**
 * `POST /directory/workers` → `{ worker }` (needs `directory:manage`). Adds a
 * roster entry — deliberately separate from inviting a login: an office
 * administrator may hold no worker row, and a worker may hold no login.
 */
export async function createWorker(input, options) {
    const body = await post('/directory/workers', input, options);
    return body.worker;
}
/** `PATCH /directory/workers/:id` → `{ worker }` (needs `directory:manage`). */
export async function updateWorker(id, patchInput, options) {
    const body = await patch(`/directory/workers/${id}`, patchInput, options);
    return body.worker;
}
/**
 * `DELETE /directory/workers/:id` → 204 (needs `directory:manage`). Soft-delete
 * (deactivate); the row is kept for history, open assignments are closed.
 */
export async function deleteWorker(id, options) {
    await del(`/directory/workers/${id}`, options);
}
/**
 * `GET /directory/workers/me` → `{ worker }`. Authenticated-only (does NOT need
 * `directory:read`) — powers `/me/profile`.
 */
export async function me(options) {
    const body = await get('/directory/workers/me', options);
    return body.worker;
}
/**
 * `GET /directory/workers/:id` → `{ worker }` (needs `directory:read`, plus
 * EITHER `user:read` or the supervisor edge).
 *
 * Unlike the roster this NAMES A SUBJECT, so it is scoped rather than refused
 * outright: your own worker row, a worker whose `worker.supervisor_id` is you,
 * or any worker with `user:read`. That matches
 * `/timekeeping/workers/:id/sessions` and `/timesheets/workers/:id/week`, which
 * the admin Worker profile screen already sits beside. Anything else is a 403,
 * including an id that does not exist — so the status cannot be used to probe
 * which workers are real.
 */
export async function worker(id, options) {
    const body = await get(`/directory/workers/${id}`, options);
    return body.worker;
}
/**
 * `GET /directory/clients` → `{ clients }` (needs `directory:read`).
 *
 * The ROSTER projection — no `dob`. See {@link Client}.
 */
export async function clients(options) {
    const body = await get('/directory/clients', options);
    return body.clients;
}
/**
 * `GET /directory/clients/:id` → `{ client }` (needs `directory:read`).
 *
 * THE READ PATH FOR ONE CLIENT, and the reason it exists is that there was not
 * one. Every field `updateClient` accepts has to be shown to the person editing
 * it, and `dob` is not on the roster — so without this call the only way to see
 * a client's current date of birth was to PATCH with an empty body, which the
 * server happily answers with the whole record. A read spelled as a write is a
 * write in the audit log (`client.updated`, service.ts:396) and a write in every
 * permission check; it is not a read that happens to be shaped oddly.
 *
 * A 404 here means the client does not exist in this tenant. A 403 means the
 * caller lacks `directory:read` — the roster and this read are gated on the same
 * key, so a caller who saw the row can read the row.
 */
export async function client(id, options) {
    const body = await get(`/directory/clients/${id}`, options);
    return body.client;
}
/**
 * `POST /directory/clients` → `{ client }` (needs `directory:manage`). The
 * response is the INITIALS-only view, same as every other client read — a name
 * typed in to create the record is never echoed back.
 *
 * `directory:manage` ALONE, unlike the update and the delete beside it, and that
 * is deliberate: a create names no existing subject, and its reply is a pure
 * function of the request body plus a generated id and the initials derived from
 * the name just typed. There is nothing in it the caller did not supply.
 */
export async function createClient(input, options) {
    const body = await post('/directory/clients', input, options);
    return body.client;
}
/**
 * `PATCH /directory/clients/:id` → `{ client }` (needs `directory:manage` AND
 * `directory:read`).
 *
 * BOTH KEYS, because this route replies with the row: an empty `{}` patch left
 * every column at its stored value and answered with the whole record, so a
 * caller holding `directory:manage` without `directory:read` read — through the
 * write door — the client `client(id)` and `clients()` both refuse them (F42).
 * A caller who is refused the read is now refused this too, with a 403 naming
 * `directory:read`, for a client that exists and one that does not alike.
 *
 * EVERY KEY IS OPTIONAL AND EVERY KEY IS MEANINGFUL. The server's
 * `updateClientSchema` (apps/api/src/domains/directory/schemas.ts:63) is a
 * `.partial()`, and the repository writes each nullable column through
 * `case when $n then $n+1 else <column> end` guarded by
 * `Object.prototype.hasOwnProperty.call(patch, key)` (repository.ts:403,417) —
 * so OMITTING a key leaves the column alone and sending `null` CLEARS it. Those
 * are different requests and a caller must not conflate them: building the body
 * with `{ dob: draft.dob || null }` silently erases a date of birth the person
 * editing never touched.
 *
 * The response is the full {@link ClientDetail}, `dob` included — the same
 * projection `client(id)` returns, not more. Do not use it as a read: see
 * {@link client}.
 */
export async function updateClient(id, patchInput, options) {
    const body = await patch(`/directory/clients/${id}`, patchInput, options);
    return body.client;
}
/**
 * `DELETE /directory/clients/:id` → 204 (needs `directory:manage` AND
 * `directory:read`). Discharges the client (soft-delete): the row is kept for
 * history, open assignments close.
 *
 * Both keys for the same reason the PATCH needs both, with the status as the
 * payload: 204-for-a-real-id and 404-for-a-made-up-one is an existence oracle
 * for client ids, and it was answered to a caller who may not be told that any
 * client exists. Refused now with the same 403 the PATCH sends, before the row
 * is looked up.
 */
export async function deleteClient(id, options) {
    await del(`/directory/clients/${id}`, options);
}
/**
 * `GET /directory/assignments` → `{ assignments }` (needs `directory:read`).
 * `openOnly: true` maps to the route's `openOnly=true` query (unclosed edges only).
 *
 * SCOPED, and the filter is not what scopes it. The caseload edge answers "which
 * member of staff serves which client", so the server decides whose caseload you
 * may read: `workerId` must be your own worker row or somebody assigned to you
 * via `worker.supervisor_id`, unless you hold `directory:read_all` (org_admin
 * only). CALL IT WITH A `workerId`. Omitting it — or passing `clientId` alone —
 * asks for the organization and is a 403 for anyone without that key, on
 * purpose: a silently narrowed list would read as "this is all there is".
 *
 * So a 403 here is an ANSWER, not a failure. Render it as a refusal
 * (`listPhase` → `refused`), never as an empty caseload.
 */
export async function assignments(filter = {}, options) {
    const body = await get('/directory/assignments', {
        ...options,
        query: { ...filter },
    });
    return body.assignments;
}
/**
 * `POST /directory/assignments` → `{ assignment }` (needs `directory:manage`,
 * plus reach over `workerId`: your own worker row, a worker whose
 * `worker.supervisor_id` is you, or `directory:read_all`).
 *
 * YOU MAY ONLY WRITE A CASELOAD EDGE YOU ARE ENTITLED TO READ — the same subject
 * scope `assignments()` enforces. `wca_open_uk` makes at most one OPEN
 * assignment per (worker, client), and re-assigning an open pairing is a 409;
 * that conflict is only reported to a caller who may already read the edge it
 * describes, because to anybody else a distinguishable answer IS the edge.
 * Out of scope is an ordinary 403, identical for a worker who does not exist.
 * Opens a worker↔client edge — the actual "assign a worker" step.
 */
export async function assignClient(input, options) {
    const body = await post('/directory/assignments', input, options);
    return body.assignment;
}
/**
 * `DELETE /directory/assignments/:id` → 204 (needs `directory:manage`, plus the
 * same reach over the worker the assignment BELONGS to — resolved server-side
 * from the row, since the subject is not in the request). An id you may not
 * reach and an id that does not exist are the SAME 403, never a 404: two
 * different answers would put the caseload oracle back, keyed on the id. Closes
 * the open edge (unassign); it is kept as history, never removed.
 */
export async function unassign(id, options) {
    await del(`/directory/assignments/${id}`, options);
}
/**
 * `POST /directory/clients/:id/reveal-name` → `{ client }` (needs
 * `client:reveal_name`). This is the ONLY path that returns a full name; the
 * backend writes an audit_event in the same transaction, so a reveal can never
 * happen unrecorded. Callers must not cache or log the returned name.
 */
export async function revealName(clientId, options) {
    const body = await post(`/directory/clients/${clientId}/reveal-name`, undefined, options);
    return body.client;
}
//# sourceMappingURL=directory.js.map